import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Brain, Zap, Target, TrendingUp, Eye, MessageCircle, Lock } from 'lucide-react';
export function PSIProBenefitsSection() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  return <section className="relative py-20 px-6 bg-gradient-to-b from-black via-purple-950/20 to-black overflow-hidden" data-id="element-3845">
      {/* Mystical background effects - matching PSIGatheringSection */}
      <div className="absolute inset-0 opacity-10" style={{
      backgroundImage: `radial-gradient(circle at 20% 50%, rgba(168, 85, 247, 0.3) 0%, transparent 50%),
                           radial-gradient(circle at 80% 50%, rgba(59, 130, 246, 0.3) 0%, transparent 50%)`
    }} data-id="element-3846" />

      {/* Glowing orbs */}
      <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl animate-pulse-slow" data-id="element-3847" />
      <div className="absolute bottom-1/4 right-1/3 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl animate-pulse-slow" style={{
      animationDelay: '1s'
    }} data-id="element-3848" />

      <motion.div ref={ref} initial={{
      opacity: 0,
      y: 30
    }} animate={inView ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.8
    }} className="relative z-10 max-w-4xl mx-auto" data-id="element-3849">
        {/* Main headline */}
        <h2 className="text-4xl md:text-6xl font-serif text-center mb-12 leading-tight text-stone-100" style={{
        fontFamily: 'Cinzel, serif',
        textShadow: '0 0 30px rgba(168, 85, 247, 0.4)'
      }} data-id="element-3850">
          <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-purple-500 bg-clip-text text-transparent" style={{
          textShadow: '0 0 40px rgba(168, 85, 247, 0.6)'
        }} data-id="element-3851">
            PSI PRO
          </span>{' '}
          Is Where The Paradigm Shift Happens
        </h2>

        <p className="text-xl text-stone-300 text-center mb-16" data-id="element-3852">
          Here's what you're actually getting when you join:
        </p>

        {/* Benefits List */}
        <div className="space-y-16" data-id="element-3853">
          {/* Benefit 1: Paradigm Shift */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="relative" data-id="element-3854">
            <div className="absolute inset-0 bg-gradient-radial from-purple-600/10 via-transparent to-transparent blur-2xl" data-id="element-3855" />

            <div className="relative bg-gradient-to-br from-purple-950/40 to-black border-2 border-purple-500/50 p-8 md:p-10 rounded-lg" data-id="element-3856">
              <div className="flex items-start gap-4 mb-6" data-id="element-3857">
                <div className="flex-shrink-0 w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center" data-id="element-3858">
                  <Brain className="w-6 h-6 text-black" data-id="element-3859" />
                </div>
                <div className="flex-1" data-id="element-3860">
                  <div className="flex items-center gap-2 mb-3" data-id="element-3861">
                    <span className="text-green-400 text-2xl" data-id="element-3862">✓</span>
                    <h3 className="text-2xl md:text-3xl font-bold text-purple-400" data-id="element-3863">
                      THE MOST IMPORTANT PARADIGM SHIFT YOU'LL EVER MAKE ABOUT
                      PEPTIDES
                    </h3>
                  </div>
                </div>
              </div>

              <div className="space-y-4 text-lg text-stone-300 pl-16" data-id="element-3864">
                <p className="text-stone-100 font-semibold" data-id="element-3865">
                  This is the shift that changes everything.
                </p>

                <div className="space-y-2 pl-6 border-l-2 border-purple-500/30" data-id="element-3866">
                  <p data-id="element-3867">
                    The shift that allows you to see what's ACTUALLY possible.
                  </p>
                  <p data-id="element-3868">
                    The shift that separates the masters from everyone else.
                  </p>
                </div>

                <p className="text-amber-400 font-bold" data-id="element-3869">
                  We've NEVER seen this covered by anyone else online. Ever.
                </p>

                <p data-id="element-3870">
                  And it's something Brenden and Ali will reveal during the live
                  strategic briefings.
                </p>

                <p className="italic text-stone-400" data-id="element-3871">
                  I can't tell you what it is on this page.
                </p>

                <p className="text-stone-400" data-id="element-3872">
                  (Because frankly, if I did, you'd think I was making it up.)
                </p>

                <div className="bg-purple-950/30 border-l-4 border-purple-500 p-6 my-6" data-id="element-3873">
                  <p className="text-xl text-stone-100 font-bold mb-3" data-id="element-3874">
                    But once you see it...
                  </p>
                  <p className="text-xl text-stone-100 font-bold mb-3" data-id="element-3875">
                    Once you UNDERSTAND it...
                  </p>
                  <p className="text-xl text-purple-400 font-bold" data-id="element-3876">
                    Everything else clicks into place.
                  </p>
                </div>

                <div className="space-y-2" data-id="element-3877">
                  <p data-id="element-3878">Your entire approach to biology changes.</p>
                  <p className="text-stone-100 font-semibold" data-id="element-3879">
                    And you'll wonder how you ever operated without knowing
                    this.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Benefit 2: The Impossible */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="relative" data-id="element-3880">
            <div className="absolute inset-0 bg-gradient-radial from-cyan-600/10 via-transparent to-transparent blur-2xl" data-id="element-3881" />

            <div className="relative bg-gradient-to-br from-cyan-950/40 to-black border-2 border-cyan-500/50 p-8 md:p-10 rounded-lg" data-id="element-3882">
              <div className="flex items-start gap-4 mb-6" data-id="element-3883">
                <div className="flex-shrink-0 w-12 h-12 bg-cyan-500 rounded-full flex items-center justify-center" data-id="element-3884">
                  <Zap className="w-6 h-6 text-black" data-id="element-3885" />
                </div>
                <div className="flex-1" data-id="element-3886">
                  <div className="flex items-center gap-2 mb-3" data-id="element-3887">
                    <span className="text-green-400 text-2xl" data-id="element-3888">✓</span>
                    <h3 className="text-2xl md:text-3xl font-bold text-cyan-400" data-id="element-3889">
                      HOW TO DO THE "IMPOSSIBLE" WITH HUMAN BIOLOGY
                    </h3>
                  </div>
                </div>
              </div>

              <div className="space-y-4 text-lg text-stone-300 pl-16" data-id="element-3890">
                <p data-id="element-3891">
                  This is where Brenden and Ali show you what's REALLY possible
                  when you understand peptides at the highest level.
                </p>

                <div className="space-y-2 pl-6 border-l-2 border-cyan-500/30" data-id="element-3892">
                  <p data-id="element-3893">Not just "optimization."</p>
                  <p data-id="element-3894">Not just "improvement."</p>
                </div>

                <p className="text-xl text-cyan-400 font-bold" data-id="element-3895">
                  But actual REGENERATION of tissues that mainstream medicine
                  considers "impossible to heal."
                </p>

                <div className="bg-cyan-950/30 p-6 rounded-lg space-y-3" data-id="element-3896">
                  <p className="text-stone-100" data-id="element-3897">
                    <span className="text-cyan-400 font-bold" data-id="element-3898">REVERSAL</span> of
                    conditions that doctors say are "permanent."
                  </p>
                  <p className="text-stone-100" data-id="element-3899">
                    <span className="text-cyan-400 font-bold" data-id="element-3900">RESTORATION</span>{' '}
                    of function that specialists say "can't be done."
                  </p>
                </div>

                <div className="space-y-2" data-id="element-3901">
                  <p className="text-stone-100 font-semibold" data-id="element-3902">
                    You'll see real case studies.
                  </p>
                  <p className="text-stone-100 font-semibold" data-id="element-3903">
                    Real transformations.
                  </p>
                  <p className="text-stone-100 font-semibold" data-id="element-3904">
                    Real "impossible" results.
                  </p>
                </div>

                <p className="text-xl text-cyan-400 font-bold" data-id="element-3905">
                  And you'll learn the EXACT frameworks they used to create
                  them.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Benefit 3: Precision Cell™ Pro */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="relative" data-id="element-3906">
            <div className="absolute inset-0 bg-gradient-radial from-emerald-600/10 via-transparent to-transparent blur-2xl" data-id="element-3907" />

            <div className="relative bg-gradient-to-br from-emerald-950/40 to-black border-2 border-emerald-500/50 p-8 md:p-10 rounded-lg" data-id="element-3908">
              <div className="flex items-start gap-4 mb-6" data-id="element-3909">
                <div className="flex-shrink-0 w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center" data-id="element-3910">
                  <Target className="w-6 h-6 text-black" data-id="element-3911" />
                </div>
                <div className="flex-1" data-id="element-3912">
                  <div className="flex items-center gap-2 mb-3" data-id="element-3913">
                    <span className="text-green-400 text-2xl" data-id="element-3914">✓</span>
                    <h3 className="text-2xl md:text-3xl font-bold text-emerald-400" data-id="element-3915">
                      THE PRECISION CELL™ PRO METHODOLOGY
                    </h3>
                  </div>
                </div>
              </div>

              <div className="space-y-4 text-lg text-stone-300 pl-16" data-id="element-3916">
                <p data-id="element-3917">
                  This is the same diagnostic and protocol design framework
                  Brenden and Ali use with private clients paying $15K−$120K.
                </p>

                <p className="text-emerald-400 font-bold" data-id="element-3918">
                  You're getting access to it as part of your PSI PRO
                  membership.
                </p>

                <p className="text-stone-100 font-semibold" data-id="element-3919">
                  Which means you'll learn:
                </p>

                <div className="space-y-3 pl-6 border-l-2 border-emerald-500/30" data-id="element-3920">
                  <p className="flex items-start gap-3" data-id="element-3921">
                    <span className="text-emerald-400 flex-shrink-0" data-id="element-3922">→</span>
                    <span data-id="element-3923">
                      How to think diagnostically instead of symptomatically
                    </span>
                  </p>
                  <p className="flex items-start gap-3" data-id="element-3924">
                    <span className="text-emerald-400 flex-shrink-0" data-id="element-3925">→</span>
                    <span data-id="element-3926">
                      How to identify root causes instead of just treating
                      surface issues
                    </span>
                  </p>
                  <p className="flex items-start gap-3" data-id="element-3927">
                    <span className="text-emerald-400 flex-shrink-0" data-id="element-3928">→</span>
                    <span data-id="element-3929">
                      How to design protocols based on molecular mechanisms
                    </span>
                  </p>
                  <p className="flex items-start gap-3" data-id="element-3930">
                    <span className="text-emerald-400 flex-shrink-0" data-id="element-3931">→</span>
                    <span data-id="element-3932">How to stack peptides for synergistic effects</span>
                  </p>
                  <p className="flex items-start gap-3" data-id="element-3933">
                    <span className="text-emerald-400 flex-shrink-0" data-id="element-3934">→</span>
                    <span data-id="element-3935">
                      How to optimize protocols based on real-time feedback
                    </span>
                  </p>
                  <p className="flex items-start gap-3" data-id="element-3936">
                    <span className="text-emerald-400 flex-shrink-0" data-id="element-3937">→</span>
                    <span data-id="element-3938">
                      How to create transformations for yourself and others
                    </span>
                  </p>
                </div>

                <p className="text-xl text-emerald-400 font-bold" data-id="element-3939">
                  This is what allows you to operate at a level most
                  practitioners will never reach.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Benefit 4: State of the Union */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="relative" data-id="element-3940">
            <div className="absolute inset-0 bg-gradient-radial from-blue-600/10 via-transparent to-transparent blur-2xl" data-id="element-3941" />

            <div className="relative bg-gradient-to-br from-blue-950/40 to-black border-2 border-blue-500/50 p-8 md:p-10 rounded-lg" data-id="element-3942">
              <div className="flex items-start gap-4 mb-6" data-id="element-3943">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center" data-id="element-3944">
                  <TrendingUp className="w-6 h-6 text-black" data-id="element-3945" />
                </div>
                <div className="flex-1" data-id="element-3946">
                  <div className="flex items-center gap-2 mb-3" data-id="element-3947">
                    <span className="text-green-400 text-2xl" data-id="element-3948">✓</span>
                    <h3 className="text-2xl md:text-3xl font-bold text-blue-400" data-id="element-3949">
                      THE STATE OF THE UNION: WHAT'S REALLY HAPPENING IN 2026
                    </h3>
                  </div>
                </div>
              </div>

              <div className="space-y-4 text-lg text-stone-300 pl-16" data-id="element-3950">
                <p data-id="element-3951">
                  Every month, Brenden and Ali will give you a STRATEGIC
                  BRIEFING about peptides, biohacking, healing or performance
                  enhancement.
                </p>

                <div className="space-y-3 pl-6 border-l-2 border-blue-500/30" data-id="element-3952">
                  <p data-id="element-3953">What's working in the real world.</p>
                  <p data-id="element-3954">What new research has emerged.</p>
                  <p data-id="element-3955">What misinformation is being spread.</p>
                  <p data-id="element-3956">Which vendors are legitimate and which are bunk.</p>
                  <p data-id="element-3957">What trends to pay attention to and what to ignore.</p>
                </div>

                <div className="bg-blue-950/30 p-6 rounded-lg space-y-3" data-id="element-3958">
                  <p className="text-xl text-blue-400 font-bold" data-id="element-3959">
                    This is your GROUNDED GUIDANCE.
                  </p>
                  <p className="text-stone-100" data-id="element-3960">
                    Your compass in a sea of noise.
                  </p>
                  <p className="text-stone-100" data-id="element-3961">
                    Your protection against the frauds, the liars, and the
                    grifters.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Benefit 5: Elite Secrets */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="relative" data-id="element-3962">
            <div className="absolute inset-0 bg-gradient-radial from-amber-600/10 via-transparent to-transparent blur-2xl" data-id="element-3963" />

            <div className="relative bg-gradient-to-br from-amber-950/40 to-black border-2 border-amber-500/50 p-8 md:p-10 rounded-lg" data-id="element-3964">
              <div className="flex items-start gap-4 mb-6" data-id="element-3965">
                <div className="flex-shrink-0 w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center" data-id="element-3966">
                  <Lock className="w-6 h-6 text-black" data-id="element-3967" />
                </div>
                <div className="flex-1" data-id="element-3968">
                  <div className="flex items-center gap-2 mb-3" data-id="element-3969">
                    <span className="text-green-400 text-2xl" data-id="element-3970">✓</span>
                    <h3 className="text-2xl md:text-3xl font-bold text-amber-400" data-id="element-3971">
                      ELITE, HIGH-LEVEL SECRETS YOU WON'T FIND ANYWHERE ELSE
                    </h3>
                  </div>
                </div>
              </div>

              <div className="space-y-4 text-lg text-stone-300 pl-16" data-id="element-3972">
                <p data-id="element-3973">
                  There are things Brenden and Ali will share during these
                  sessions that you will NEVER find outside of this membership.
                </p>

                <div className="space-y-3 pl-6 border-l-2 border-amber-500/30" data-id="element-3974">
                  <p data-id="element-3975">
                    Insights that would fundamentally change how you think about
                    biology.
                  </p>
                  <p data-id="element-3976">
                    Secrets that sound too good to be true... until you see the
                    science behind them.
                  </p>
                  <p data-id="element-3977">
                    Strategies that are so effective they border on unfair
                    advantages.
                  </p>
                </div>

                <p className="italic text-stone-400" data-id="element-3978">
                  I can't reveal what these are on this page.
                </p>

                <div className="bg-amber-950/30 p-6 rounded-lg" data-id="element-3979">
                  <p className="text-xl text-stone-100 font-bold mb-3" data-id="element-3980">
                    But I can tell you this:
                  </p>
                  <div className="space-y-2" data-id="element-3981">
                    <p data-id="element-3982">
                      If you're the kind of person who values being 10-20 years
                      ahead of the mainstream...
                    </p>
                    <p data-id="element-3983">
                      If you want access to knowledge that gives you a REAL
                      edge...
                    </p>
                    <p className="text-xl text-amber-400 font-bold" data-id="element-3984">
                      Then this alone is worth the price of admission.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Benefit 6: Monthly Q&A */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.5
        }} className="relative" data-id="element-3985">
            <div className="absolute inset-0 bg-gradient-radial from-rose-600/10 via-transparent to-transparent blur-2xl" data-id="element-3986" />

            <div className="relative bg-gradient-to-br from-rose-950/40 to-black border-2 border-rose-500/50 p-8 md:p-10 rounded-lg" data-id="element-3987">
              <div className="flex items-start gap-4 mb-6" data-id="element-3988">
                <div className="flex-shrink-0 w-12 h-12 bg-rose-500 rounded-full flex items-center justify-center" data-id="element-3989">
                  <MessageCircle className="w-6 h-6 text-black" data-id="element-3990" />
                </div>
                <div className="flex-1" data-id="element-3991">
                  <div className="flex items-center gap-2 mb-3" data-id="element-3992">
                    <span className="text-green-400 text-2xl" data-id="element-3993">✓</span>
                    <h3 className="text-2xl md:text-3xl font-bold text-rose-400" data-id="element-3994">
                      MONTHLY Q&A SESSIONS WHERE YOU CAN GET YOUR OWN QUESTIONS
                      ANSWERED
                    </h3>
                  </div>
                </div>
              </div>

              <div className="space-y-4 text-lg text-stone-300 pl-16" data-id="element-3995">
                <p className="text-xl text-rose-400 font-bold" data-id="element-3996">
                  This is where PSI PRO becomes PERSONAL.
                </p>

                <p data-id="element-3997">You're not just watching pre-recorded content.</p>

                <p className="text-stone-100 font-semibold" data-id="element-3998">
                  You're getting DIRECT ACCESS to Brenden and Ali.
                </p>

                <div className="space-y-3 pl-6 border-l-2 border-rose-500/30" data-id="element-3999">
                  <p data-id="element-4000">You can submit your questions in advance.</p>
                  <p data-id="element-4001">You can ask them live during the sessions.</p>
                  <p className="text-stone-100 font-semibold" data-id="element-4002">
                    You can get answers about YOUR specific situation.
                  </p>
                </div>

                <div className="bg-rose-950/30 p-6 rounded-lg space-y-3" data-id="element-4003">
                  <p className="text-stone-100 font-bold" data-id="element-4004">YOUR protocols.</p>
                  <p className="text-stone-100 font-bold" data-id="element-4005">YOUR results.</p>
                  <p className="text-stone-100 font-bold" data-id="element-4006">YOUR challenges.</p>
                </div>

                <p className="text-xl text-rose-400 font-bold" data-id="element-4007">
                  This is similar to the kind of access that private clients pay
                  $1,500/hour for.
                </p>

                <p className="text-stone-100 font-semibold" data-id="element-4008">
                  You're getting it as part of your membership.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </section>;
}